import React, { useState, useEffect } from 'react';
import { adminAPI } from '../../lib/api';
import { Users, Package, MessageCircle, Flag, DollarSign, TrendingUp, Zap, AlertTriangle, BarChart3, ShoppingCart, Activity } from 'lucide-react';
import { Link } from 'react-router-dom';
import { AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts';

function PromoAnalyticsSection() {
  const [analytics, setAnalytics] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    adminAPI.getPromotionAnalytics()
      .then(r => setAnalytics(r.data))
      .catch(console.error)
      .finally(() => setLoading(false));
  }, []);

  if (loading) return (
    <div className="space-y-4 animate-pulse">
      <div className="h-6 bg-zinc-100 rounded w-48" />
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        {[...Array(4)].map((_, i) => <div key={i} className="bg-white rounded-lg border border-zinc-200 p-5"><div className="h-4 bg-zinc-100 rounded w-2/3 mb-3" /><div className="h-7 bg-zinc-100 rounded w-1/3" /></div>)}
      </div>
      <div className="bg-white rounded-lg border border-zinc-200 p-6 h-64" />
    </div>
  );

  if (!analytics) return null;

  const summaryCards = [
    { label: 'All-Time Revenue', value: `$${analytics.total_revenue.toLocaleString()}`, sub: `${analytics.total_purchases} purchases`, icon: DollarSign, color: 'bg-emerald-50 text-emerald-600' },
    { label: '30-Day Revenue', value: `$${analytics.revenue_30d.toLocaleString()}`, sub: `${analytics.purchases_30d} purchases`, icon: TrendingUp, color: 'bg-blue-50 text-blue-600' },
    { label: 'Active Promotions', value: analytics.active_promotions, sub: 'currently running', icon: Zap, color: 'bg-amber-50 text-amber-600' },
    { label: 'Total Purchased', value: analytics.total_purchases, sub: 'all time', icon: ShoppingCart, color: 'bg-violet-50 text-violet-600' },
  ];

  const chartData = analytics.daily_trend.map(d => ({
    ...d,
    label: new Date(d.date + 'T00:00:00').toLocaleDateString('en-US', { month: 'short', day: 'numeric' }),
  }));

  const maxRevenue = Math.max(...chartData.map(d => d.revenue), 1);

  return (
    <div data-testid="promo-analytics-section">
      <div className="flex items-center gap-2 mb-4">
        <BarChart3 className="w-5 h-5 text-[#002FA7]" />
        <h2 className="text-lg font-bold text-zinc-900">Promotion Performance</h2>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
        {summaryCards.map(card => {
          const Icon = card.icon;
          return (
            <div key={card.label} className="bg-white rounded-lg border border-zinc-200 p-5" data-testid={`promo-stat-${card.label.toLowerCase().replace(/[^a-z]/g, '-')}`}>
              <div className="flex items-center justify-between mb-2">
                <span className="text-[10px] font-bold uppercase tracking-wider text-zinc-400">{card.label}</span>
                <div className={`w-7 h-7 rounded-lg flex items-center justify-center ${card.color}`}>
                  <Icon className="w-3.5 h-3.5" />
                </div>
              </div>
              <p className="text-xl font-black text-zinc-900">{card.value}</p>
              <p className="text-[11px] text-zinc-400 mt-0.5">{card.sub}</p>
            </div>
          );
        })}
      </div>

      <div className="grid lg:grid-cols-3 gap-6">
        {/* Revenue Trend Chart */}
        <div className="lg:col-span-2 bg-white rounded-lg border border-zinc-200 p-6" data-testid="revenue-trend-chart">
          <div className="flex items-center justify-between mb-4">
            <div>
              <h3 className="font-bold text-zinc-900 text-sm">Revenue Trend</h3>
              <p className="text-xs text-zinc-400">Last 30 days</p>
            </div>
            <Activity className="w-4 h-4 text-zinc-300" />
          </div>
          {analytics.revenue_30d === 0 ? (
            <div className="h-48 flex items-center justify-center text-sm text-zinc-400">
              No promotion revenue in the last 30 days
            </div>
          ) : (
            <div className="h-48">
              <ResponsiveContainer width="100%" height="100%">
                <AreaChart data={chartData} margin={{ top: 4, right: 4, left: -20, bottom: 0 }}>
                  <defs>
                    <linearGradient id="revenueGrad" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="5%" stopColor="#002FA7" stopOpacity={0.15} />
                      <stop offset="95%" stopColor="#002FA7" stopOpacity={0} />
                    </linearGradient>
                  </defs>
                  <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                  <XAxis dataKey="label" tick={{ fontSize: 10, fill: '#a1a1aa' }} tickLine={false} axisLine={false} interval={Math.floor(chartData.length / 6)} />
                  <YAxis tick={{ fontSize: 10, fill: '#a1a1aa' }} tickLine={false} axisLine={false} domain={[0, maxRevenue * 1.1]} tickFormatter={v => `$${v}`} />
                  <Tooltip
                    contentStyle={{ borderRadius: '8px', border: '1px solid #e4e4e7', fontSize: '12px', boxShadow: '0 4px 12px rgba(0,0,0,0.08)' }}
                    formatter={(value) => [`$${value.toFixed(2)}`, 'Revenue']}
                    labelFormatter={(label) => label}
                  />
                  <Area type="monotone" dataKey="revenue" stroke="#002FA7" strokeWidth={2} fill="url(#revenueGrad)" />
                </AreaChart>
              </ResponsiveContainer>
            </div>
          )}
        </div>

        {/* Tier Breakdown */}
        <div className="bg-white rounded-lg border border-zinc-200 p-6" data-testid="tier-breakdown">
          <div className="flex items-center justify-between mb-4">
            <div>
              <h3 className="font-bold text-zinc-900 text-sm">Revenue by Tier</h3>
              <p className="text-xs text-zinc-400">All-time breakdown</p>
            </div>
          </div>
          {analytics.by_tier.length === 0 ? (
            <div className="h-48 flex items-center justify-center text-sm text-zinc-400">
              No promotion purchases yet
            </div>
          ) : (
            <div className="space-y-3">
              {analytics.by_tier.map((tier, idx) => {
                const pct = analytics.total_revenue > 0 ? (tier.revenue / analytics.total_revenue * 100) : 0;
                return (
                  <div key={idx} data-testid={`tier-breakdown-${idx}`}>
                    <div className="flex items-center justify-between mb-1">
                      <span className="text-sm font-medium text-zinc-700 truncate max-w-[140px]">{tier.name}</span>
                      <span className="text-xs font-mono font-semibold text-zinc-900">${tier.revenue.toFixed(2)}</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <div className="flex-1 h-2 bg-zinc-100 rounded-full overflow-hidden">
                        <div className="h-full bg-[#002FA7] rounded-full transition-all" style={{ width: `${Math.max(pct, 2)}%` }} />
                      </div>
                      <span className="text-[10px] text-zinc-400 w-16 text-right">{tier.count} sold</span>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export default function AdminOverview() {
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    adminAPI.getStats().then(r => setStats(r.data)).catch(console.error).finally(() => setLoading(false));
  }, []);

  if (loading) return (
    <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
      {[...Array(8)].map((_, i) => <div key={i} className="bg-white rounded-lg p-6 animate-pulse"><div className="h-4 bg-zinc-100 rounded w-1/2 mb-3" /><div className="h-8 bg-zinc-100 rounded w-1/3" /></div>)}
    </div>
  );

  if (!stats) return <p className="text-zinc-500">Failed to load stats</p>;

  const cards = [
    { label: 'Total Users', value: stats.total_users, icon: Users, color: 'bg-blue-50 text-blue-600', link: '/admin/users' },
    { label: 'Active Listings', value: stats.total_listings, icon: Package, color: 'bg-emerald-50 text-emerald-600', link: '/admin/listings' },
    { label: 'Messages Sent', value: stats.total_messages, icon: MessageCircle, color: 'bg-violet-50 text-violet-600' },
    { label: 'Total Revenue', value: `$${stats.total_revenue}`, icon: DollarSign, color: 'bg-amber-50 text-amber-600', link: '/admin/transactions' },
    { label: 'Pending Reports', value: stats.pending_reports, icon: AlertTriangle, color: stats.pending_reports > 0 ? 'bg-red-50 text-red-600' : 'bg-zinc-50 text-zinc-500', link: '/admin/reports' },
    { label: 'Promoted Listings', value: stats.promoted_listings, icon: Zap, color: 'bg-indigo-50 text-indigo-600' },
    { label: 'New Users (7d)', value: stats.new_users_week, icon: TrendingUp, color: 'bg-teal-50 text-teal-600' },
    { label: 'New Listings (7d)', value: stats.new_listings_week, icon: Package, color: 'bg-orange-50 text-orange-600' },
  ];

  return (
    <div data-testid="admin-overview">
      <div className="mb-8">
        <h1 className="text-2xl sm:text-3xl font-bold tracking-tight text-zinc-900">Dashboard</h1>
        <p className="text-sm text-zinc-500 mt-1">Overview of your marketplace</p>
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
        {cards.map(card => {
          const Icon = card.icon;
          const Wrapper = card.link ? Link : 'div';
          return (
            <Wrapper
              key={card.label}
              to={card.link || '#'}
              className="bg-white rounded-lg border border-zinc-200 p-5 hover:shadow-md transition-shadow"
              data-testid={`stat-${card.label.toLowerCase().replace(/[^a-z]/g, '-')}`}
            >
              <div className="flex items-center justify-between mb-3">
                <span className="text-xs font-bold uppercase tracking-wider text-zinc-500">{card.label}</span>
                <div className={`w-8 h-8 rounded-lg flex items-center justify-center ${card.color}`}>
                  <Icon className="w-4 h-4" />
                </div>
              </div>
              <p className="text-2xl font-black text-zinc-900">{card.value}</p>
            </Wrapper>
          );
        })}
      </div>

      {/* Promotion Performance Analytics */}
      <div className="mb-8">
        <PromoAnalyticsSection />
      </div>

      {/* Quick Actions */}
      <div className="bg-white rounded-lg border border-zinc-200 p-6">
        <h2 className="font-bold text-zinc-900 mb-4">Quick Actions</h2>
        <div className="grid sm:grid-cols-3 gap-3">
          <Link to="/admin/users" className="flex items-center gap-3 p-3 rounded-lg border border-zinc-200 hover:border-[#002FA7] hover:bg-[#002FA7]/5 transition-colors" data-testid="quick-manage-users">
            <Users className="w-5 h-5 text-[#002FA7]" />
            <div>
              <p className="text-sm font-semibold text-zinc-900">Manage Users</p>
              <p className="text-xs text-zinc-500">View, ban, verify accounts</p>
            </div>
          </Link>
          <Link to="/admin/reports" className="flex items-center gap-3 p-3 rounded-lg border border-zinc-200 hover:border-[#002FA7] hover:bg-[#002FA7]/5 transition-colors" data-testid="quick-review-reports">
            <Flag className="w-5 h-5 text-[#002FA7]" />
            <div>
              <p className="text-sm font-semibold text-zinc-900">Review Reports</p>
              <p className="text-xs text-zinc-500">{stats.pending_reports} pending</p>
            </div>
          </Link>
          <Link to="/admin/promotion-tiers" className="flex items-center gap-3 p-3 rounded-lg border border-zinc-200 hover:border-[#002FA7] hover:bg-[#002FA7]/5 transition-colors" data-testid="quick-promotion-tiers">
            <Zap className="w-5 h-5 text-[#002FA7]" />
            <div>
              <p className="text-sm font-semibold text-zinc-900">Promotion Tiers</p>
              <p className="text-xs text-zinc-500">Manage pricing packages</p>
            </div>
          </Link>
        </div>
      </div>
    </div>
  );
}
